/**
 * `<RemindersTab/>` — the drawer's Reminders tab: set + list forward nudges on
 * ONE node (B4.2, the node-scoped half).
 *
 * Issue #768 (Phase 4). A reminder is a FORWARD nudge — "nudge about this on
 * <date>" — never time-logging, never "when did X happen", never a per-person
 * activity record (§A1.3 / §A9). The tab shows:
 *   - a "Remind…" composer: a future date/time (`remind_at`), an optional
 *     "nudge whom" target (default: whoever can act / self), and an optional
 *     calm note;
 *   - the node's existing reminders (soonest first), each with the target
 *     resolved to a name, the lead phrase ("in 2 days"), the optional note, and
 *     a clear ✕ shown ONLY where the actor may clear (setter or lead — mirrors
 *     the backend 403 rule, so we never paint a button the API would reject).
 *
 * A past date is blocked client-side with a calm message (the backend would
 * 422); a future date posts. The view is presentational over `useReminders`
 * (data ⟂ view, §A7): it receives the list + states + the member resolver and
 * never calls `apiFetch`.
 */

import { useMemo, useState } from "react";

import type { TaskReminder } from "../api/useReminders";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";
import {
  canClearReminder,
  formatRemindDate,
  formatRemindLead,
  isFutureInstant,
} from "./reminderText";

export interface RemindersTabProps {
  /** This node's reminders, soonest-first (as the endpoint returns). */
  reminders: TaskReminder[];
  /** True while the list is loading. */
  isLoading: boolean;
  /** True on a fetch failure (incl. a not-yet-deployed / ungated 404). */
  isError: boolean;
  /** The team roster — resolves a nudge's target / setter id → name / initials. */
  members: TaskMember[];
  /** The signed-in actor's membership id (null if unresolved) — drives clear. */
  currentMembershipId: string | null;
  /** Whether the actor is a lead / co_lead — a lead may clear any reminder. */
  isLead: boolean;
  /** Set a forward nudge (a future instant + optional target + optional note). */
  onSet: (args: { remind_at: string; target_membership_id?: string; note?: string }) => void;
  /** True while a set is in flight (disables the composer). */
  isSetting: boolean;
  /** Clear a reminder by id (only ever called for a clearable one). */
  onClear: (reminderId: string) => void;
}

export function RemindersTab({
  reminders,
  isLoading,
  isError,
  members,
  currentMembershipId,
  isLead,
  onSet,
  isSetting,
  onClear,
}: RemindersTabProps) {
  const theme = useMapTheme();
  const memberById = useMemo(() => new Map(members.map((m) => [m.membershipId, m])), [members]);

  return (
    <div className="flex h-full flex-col" data-testid="drawer-reminders-tab">
      {/* The existing nudges (scrolls); the composer stays pinned below. */}
      <div
        className={
          theme.mode === "editorial"
            ? "min-h-0 flex-1 space-y-2.5 overflow-auto"
            : "min-h-0 flex-1 overflow-auto"
        }
        data-testid="reminders-list"
      >
        {isError ? (
          <RemindersUnavailable theme={theme} />
        ) : isLoading ? (
          <RemindersLoading theme={theme} />
        ) : reminders.length === 0 ? (
          <RemindersEmpty theme={theme} />
        ) : (
          <ul className={theme.mode === "editorial" ? "flex flex-col gap-2.5" : "flex flex-col"}>
            {reminders.map((reminder, i) => (
              <ReminderItem
                key={reminder.id}
                reminder={reminder}
                memberById={memberById}
                isLast={i === reminders.length - 1}
                canClear={canClearReminder(reminder, currentMembershipId, isLead)}
                onClear={onClear}
                theme={theme}
              />
            ))}
          </ul>
        )}
      </div>

      {/* §A9 footer: reminders are a forward nudge, on the member's own terms —
          editorial surfaces the same firewall line the wireframe (V_B42) carries
          below the nudge rows. */}
      {theme.mode === "editorial" && (
        <div
          data-testid="reminders-footer"
          className="px-1 pt-3 text-center text-[11px] italic"
          style={{ color: theme.palette.muted }}
        >
          reminders are about the work, on your terms
        </div>
      )}

      <ReminderComposer members={members} disabled={isSetting} onSet={onSet} theme={theme} />
    </div>
  );
}

// ── One reminder in the list ──────────────────────────────────────────────────

function ReminderItem({
  reminder,
  memberById,
  isLast,
  canClear,
  onClear,
  theme,
}: {
  reminder: TaskReminder;
  memberById: Map<string, TaskMember>;
  isLast: boolean;
  canClear: boolean;
  onClear: (reminderId: string) => void;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  // A forward nudge reads as a calm AMBER banner (the wireframe DUE-SOON row) —
  // never alarm red (§A9 / brand firewall). `--st-block-bg` #F5E6CC wash + the
  // .t-banner.amber hairline.
  const AMBER_BG = "#F5E6CC";
  const AMBER_BORDER = "rgba(201,133,45,0.4)";

  // WHO the nudge is for — a single target, never a count about a person (§A9).
  const target = reminder.target_membership_id
    ? memberById.get(reminder.target_membership_id)
    : undefined;
  const targetName = reminder.target_membership_id
    ? (target?.name ?? "a teammate")
    : "whoever can act";

  return (
    <li
      data-testid="reminder-item"
      data-reminder-id={reminder.id}
      className={
        editorial
          ? "flex items-center gap-3.5 rounded-[10px] border px-4 py-3.5"
          : "flex gap-2.5 py-3"
      }
      style={
        editorial
          ? { backgroundColor: AMBER_BG, borderColor: AMBER_BORDER }
          : isLast
            ? undefined
            : { borderBottom: `1px solid ${c.cardBorder}` }
      }
    >
      {/* Editorial: a mono glyph disc (the B4.2 nudge kicker glyph); legacy: the
          plain 🔔 lead glyph. */}
      {editorial ? (
        <span
          aria-hidden
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-base"
          style={{
            backgroundColor: "rgba(255,255,255,0.6)",
            color: c.ink,
            fontFamily: theme.fonts.mono,
          }}
        >
          🔔
        </span>
      ) : (
        <span aria-hidden className="mt-0.5 shrink-0 text-sm" style={{ color: c.teal }}>
          🔔
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          {/* Forward framing: "Nudge <name> on <date>" — never an elapsed log. */}
          <span
            className="truncate text-xs font-semibold"
            style={{ color: c.ink }}
            data-testid="reminder-summary"
          >
            Nudge {targetName} on {formatRemindDate(reminder.remind_at)}
          </span>
          <span
            className="shrink-0 text-[10px]"
            style={{
              color: editorial ? c.muted : c.faint,
              fontFamily: editorial ? theme.fonts.mono : undefined,
            }}
            data-testid="reminder-lead"
          >
            {formatRemindLead(reminder.remind_at)}
          </span>
        </div>
        {reminder.note && (
          <p
            className="mt-0.5 whitespace-pre-wrap break-words text-xs"
            style={{ color: c.slate }}
            data-testid="reminder-note"
          >
            {reminder.note}
          </p>
        )}
      </div>
      {canClear && (
        <button
          type="button"
          data-testid="reminder-clear"
          aria-label="Clear this reminder"
          onClick={() => onClear(reminder.id)}
          className="h-fit shrink-0 rounded p-0.5 text-xs leading-none transition-colors hover:bg-slate-100"
          style={{ color: editorial ? c.muted : c.faint }}
        >
          ✕
        </button>
      )}
    </li>
  );
}

// ── The composer (date + target + note + set) ─────────────────────────────────

function ReminderComposer({
  members,
  disabled,
  onSet,
  theme,
}: {
  members: TaskMember[];
  disabled: boolean;
  onSet: (args: { remind_at: string; target_membership_id?: string; note?: string }) => void;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  // The native datetime-local value (local wall-clock, no tz suffix).
  const [when, setWhen] = useState("");
  // "" = no specific target (whoever can act); else a membership id.
  const [targetId, setTargetId] = useState("");
  const [note, setNote] = useState("");
  // A calm, in-line message when the chosen date isn't in the future.
  const [error, setError] = useState<string | null>(null);

  const set = () => {
    if (disabled) return;
    if (!when) {
      setError("Pick a date to be nudged.");
      return;
    }
    // A reminder points forward (§A1.3). Validate client-side too, so a past
    // date gets a calm message rather than bouncing off the backend's 422.
    if (!isFutureInstant(when)) {
      setError("That date has already passed — pick a future date.");
      return;
    }
    setError(null);
    // datetime-local has no timezone; serialise it as a full ISO instant so the
    // backend reads an unambiguous moment.
    onSet({
      remind_at: new Date(when).toISOString(),
      ...(targetId ? { target_membership_id: targetId } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    setWhen("");
    setTargetId("");
    setNote("");
  };

  // One field skin, shared by the date/select/note controls. Editorial: a
  // `.t-input` (cream surface, rule-2 hairline); legacy: the shipped neutral
  // grey field (byte-identical).
  const fieldStyle = editorial
    ? {
        borderColor: c.rule2,
        backgroundColor: c.cardBg,
        color: c.ink,
        fontFamily: theme.fonts.sans,
      }
    : { borderColor: c.cardBorder, backgroundColor: "#F7F8FA", color: c.ink };
  const labelStyle = {
    color: c.faint,
    ...(editorial ? { fontFamily: theme.fonts.mono } : {}),
  };

  return (
    <div
      className="border-t pt-3"
      style={
        editorial
          ? {
              borderColor: c.rule,
              backgroundColor: c.bgRecess,
              marginLeft: -12,
              marginRight: -12,
              marginBottom: -12,
              paddingLeft: 12,
              paddingRight: 12,
              paddingBottom: 12,
            }
          : { borderColor: c.cardBorder }
      }
    >
      <div
        className="mb-2 text-[11px] font-semibold"
        style={{
          color: c.slate,
          ...(editorial
            ? {
                fontFamily: theme.fonts.mono,
                textTransform: "uppercase" as const,
                letterSpacing: "0.06em",
              }
            : {}),
        }}
      >
        Remind about this
      </div>

      {/* When */}
      <label className="mb-2 block text-[10px]" style={labelStyle}>
        <span className="mb-1 block">on</span>
        <input
          type="datetime-local"
          data-testid="reminder-when"
          value={when}
          disabled={disabled}
          onChange={(e) => {
            setWhen(e.target.value);
            if (error) setError(null);
          }}
          className="w-full rounded-lg border p-2 text-sm outline-none disabled:opacity-60"
          style={fieldStyle}
        />
      </label>

      {/* Whom (optional) */}
      <label className="mb-2 block text-[10px]" style={labelStyle}>
        <span className="mb-1 block">nudge</span>
        <select
          data-testid="reminder-target"
          value={targetId}
          disabled={disabled}
          onChange={(e) => setTargetId(e.target.value)}
          className="w-full rounded-lg border p-2 text-sm outline-none disabled:opacity-60"
          style={fieldStyle}
        >
          <option value="">Whoever can act</option>
          {members.map((m) => (
            <option key={m.membershipId} value={m.membershipId}>
              {m.name}
            </option>
          ))}
        </select>
      </label>

      {/* Note (optional) */}
      <textarea
        data-testid="reminder-note-input"
        value={note}
        disabled={disabled}
        onChange={(e) => setNote(e.target.value)}
        placeholder="add a short note (optional)…"
        rows={2}
        className="w-full resize-none rounded-lg border p-2.5 text-sm outline-none disabled:opacity-60"
        style={fieldStyle}
      />

      {error && (
        <div
          className="mt-1.5 text-[11px]"
          data-testid="reminder-error"
          style={{ color: editorial ? "#8E5A1A" : "#B45309" }}
        >
          {error}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between">
        <span
          className="text-[10px]"
          style={{
            color: c.faint,
            ...(editorial ? { fontFamily: theme.fonts.mono } : {}),
          }}
        >
          a gentle forward nudge — never a record of hours
        </span>
        <button
          type="button"
          data-testid="reminder-set"
          onClick={set}
          disabled={disabled}
          className="rounded-md px-4 py-1.5 text-[11px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-40"
          // Editorial: an ink `.t-btn` (cream label); legacy: the shipped solid
          // teal button (white label).
          style={{
            backgroundColor: editorial ? c.ink : c.teal,
            color: editorial ? c.canvas : "#FFFFFF",
          }}
        >
          Remind me
        </button>
      </div>
    </div>
  );
}

// ── States ────────────────────────────────────────────────────────────────────

function RemindersEmpty({ theme }: { theme: MapTheme }) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div
      data-testid="reminders-empty"
      className="flex flex-col items-center justify-center gap-1 py-10 text-center"
    >
      <span className="text-xl" aria-hidden>
        🔔
      </span>
      <div
        className="text-sm font-semibold"
        style={{
          color: c.slate,
          fontFamily: editorial ? theme.fonts.serif : undefined,
          fontStyle: editorial ? "italic" : undefined,
        }}
      >
        No reminders yet
      </div>
      <div className="max-w-[18rem] text-xs" style={{ color: c.faint }}>
        Set a gentle nudge for a date ahead.
      </div>
    </div>
  );
}

function RemindersLoading({ theme }: { theme: MapTheme }) {
  return (
    <div
      data-testid="reminders-loading"
      className="py-10 text-center text-xs"
      style={{ color: theme.palette.faint }}
    >
      Loading reminders…
    </div>
  );
}

function RemindersUnavailable({ theme }: { theme: MapTheme }) {
  return (
    <div
      data-testid="reminders-unavailable"
      className="flex flex-col items-center justify-center gap-1 py-10 text-center"
    >
      <span className="text-xl" aria-hidden>
        🔔
      </span>
      <div className="text-sm" style={{ color: theme.palette.muted }}>
        Reminders unavailable
      </div>
    </div>
  );
}
